/**
 * Auth context — holds the current user + JWT, persists the token in localStorage,
 * validates it on load via /api/auth/me, and attaches it to all axios requests.
 */
import React from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "pari_token";

const AuthContext = React.createContext(null);
export const useAuth = () => React.useContext(AuthContext);

const setAuthHeader = (token) => {
  if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete axios.defaults.headers.common.Authorization;
};

export function AuthProvider({ children }) {
  const [user, setUser] = React.useState(null);
  const [token, setToken] = React.useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = React.useState(true);

  // Validate any persisted token on first load
  React.useEffect(() => {
    let alive = true;
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setLoading(false); return; }
    setAuthHeader(t);
    axios.get(`${API}/auth/me`)
      .then((r) => { if (alive) { setUser(r.data.user); setToken(t); } })
      .catch(() => { if (alive) { localStorage.removeItem(TOKEN_KEY); setAuthHeader(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Global 401 handler: the API rejects invalid/expired sessions on every
  // route, so a 401 outside the auth endpoints means the session is gone —
  // clear it so the app falls back to the login screen instead of a dead page.
  React.useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err.response?.status;
        const url = String(err.config?.url || "");
        if (status === 401 && !url.includes("/auth/")) {
          localStorage.removeItem(TOKEN_KEY);
          setAuthHeader(null);
          setToken(null);
          setUser(null);
        }
        return Promise.reject(err);
      });
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = React.useCallback((tok, usr) => {
    localStorage.setItem(TOKEN_KEY, tok);
    setAuthHeader(tok);
    setToken(tok);
    setUser(usr);
  }, []);

  const logout = React.useCallback(() => {
    try { axios.post(`${API}/auth/logout`).catch(() => {}); } catch { /* noop */ }
    localStorage.removeItem(TOKEN_KEY);
    setAuthHeader(null);
    setToken(null);
    setUser(null);
  }, []);

  const value = { user, token, loading, login, logout, API };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
